import { useEffect, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { MaxChrome } from './components/MaxChrome';
import { HomePage } from './pages/HomePage';
import { TicketPage } from './pages/TicketPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { readTicketDeepLinkId } from './lib/maxWebApp';

/** Once we open a ticket from startapp/ticket_id, don't re-force it when user goes home. */
const TICKET_DEEPLINK_CONSUMED_KEY = 'nb_ticket_deeplink_consumed';

/** sessionStorage may fail in MAX WebView — keep an in-memory fallback for the SPA life. */
const consumedTicketDeepLinks = new Set<string>();

function wasTicketDeepLinkConsumed(ticketId: string): boolean {
  if (consumedTicketDeepLinks.has(ticketId)) return true;
  try {
    return sessionStorage.getItem(TICKET_DEEPLINK_CONSUMED_KEY) === ticketId;
  } catch {
    return false;
  }
}

function markTicketDeepLinkConsumed(ticketId: string): void {
  consumedTicketDeepLinks.add(ticketId);
  try {
    sessionStorage.setItem(TICKET_DEEPLINK_CONSUMED_KEY, ticketId);
  } catch {
    // ignore private mode / credentialless
  }
}

function ticketIdFromPath(pathname: string): string | null {
  const match = /^\/tickets\/(\d+)\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

function LegacyTicketRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const markedForPath = useRef<string | null>(null);

  useEffect(() => {
    const deepLinkTicket = readTicketDeepLinkId();
    const pathTicket = ticketIdFromPath(location.pathname);

    // Direct open from bot push lands on /tickets/:id — mark consumed immediately,
    // otherwise the first "Back" to home re-applies the same start_param deep-link.
    if (deepLinkTicket && pathTicket === deepLinkTicket) {
      if (markedForPath.current !== deepLinkTicket) {
        markTicketDeepLinkConsumed(deepLinkTicket);
        markedForPath.current = deepLinkTicket;
      }
      return;
    }

    const isHome = location.pathname === '/' || location.pathname === '';
    if (!isHome) return;
    if (!deepLinkTicket) return;
    if (wasTicketDeepLinkConsumed(deepLinkTicket)) return;

    markTicketDeepLinkConsumed(deepLinkTicket);
    markedForPath.current = deepLinkTicket;

    const params = new URLSearchParams(location.search);
    params.delete('ticket_id');
    params.delete('ticketId');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: `/tickets/${deepLinkTicket}`,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}

export function App() {
  return (
    <>
      <MaxChrome />
      <LegacyTicketRedirect />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/tickets/:id" element={<TicketPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
